// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import type { DispatchType } from "../../global/types/redux";
import type { ErrorsType, PasswordStrengthType } from "../types/UserFormType";
import { ErrosDisplay, checkPassword, setErrorMsg } from "../containers/ErrosDisplay";
import { SingleTextField, CancelButton, SubmitButton } from "imagus-components";
import { PasswordStrengthBox, checkPswStrength } from "../../global/components/PasswordStrength";
import { logout, fetchAuthentication } from "../../account/actions/userActions";
import { userLogin, userConfirm } from "../actions/profileActions";
import BlueLogoImage from "../../../images/VixVizion.svg";

import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import EmailIcon from "@material-ui/icons/Email";
import PersonIcon from "@material-ui/icons/Person";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";

const DEBOUNCETIME = 500;

type InviteFormStatesType = {
  stage: 0 | 1,
  errors: ErrorsType,
  username: string,
  tempPassword: string,
  firstName: string,
  lastName: string,
  password: string,
  password2: string,
  loading: boolean,
  passwordstrength: PasswordStrengthType
};

class InviteForm extends Component<*, InviteFormStatesType> {
  timeout: number;
  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
    this.state = {
      stage: 0,
      errors: {},
      loading: false,
      username: props.match.params.token ? props.match.params.token : "",
      tempPassword: "",
      firstName: "",
      lastName: "",
      password: "",
      password2: "",
      passwordstrength: {
        uppercase: false,
        lowercase: false,
        number: false,
        length: false
      }
    };
  }

  componentDidMount() {
    this.props.actions.fetchAuthentication(false, this.callLogout);
  }

  callLogout = (authenticated: boolean): * => {
    if (authenticated) {
      logout((): * => console.log("logged out"));
    }
  }

  handleChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.setState((state: ForgotStatesType) => ({
      [name]: value
    }));

    //password strength check
    if (name === "password") {
      this.setState({
        passwordstrength: checkPswStrength(this.state.passwordstrength, value)
      });
    }

    //clear credital error msg
    if (name === "tempPassword") {
      if (this.state.errors.credentials !== "") {
        this.setState({ errors: setErrorMsg(this.state.errors, "credentials", "", true) });
      }
    }

    if (name === "password" || name === "password2") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(function () {
        this.setState({ errors: checkPassword(this.state.errors, this.state.password, this.state.password2) });
      }.bind(this), DEBOUNCETIME);
    }
  }

  submit = async (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((): * => ({ "loading": true }));

    const { username, tempPassword, errors, stage, password, password2, passwordstrength, firstName, lastName } = this.state;

    if (stage === 0) {
      if (Object.keys(errors).length === 0) {
        this.props.actions.userLogin(
          username,
          tempPassword,
          (user_names: *): * => {
            this.setState({
              stage: 1,
              firstName: user_names.first_name,
              lastName: user_names.last_name
            });
          },
          (errors: ErrorsType): * => {
            this.setState({
              errors: errors
            });
          }
        );
      }
    }
    else {
      if (Object.keys(errors).length === 0 && !Object.values(passwordstrength).includes(false)) {
        this.props.actions.userConfirm(
          firstName,
          lastName,
          "",
          password,
          (): * => {
            window.location.href = "/account/profile";
          },
          (errors: ErrorsType): * => {
            this.setState({
              errors: errors
            });
          }
        );
      }
    }

    this.setState((): * => ({ "loading": false }));
  }

  render(): React$Element<*> {
    const { errors, loading, stage } = this.state;
    return (
      <div className="wrapper">
        {stage === 0 &&
          <AppBar position="static" color="primary">
            <Toolbar>
              <EmailIcon className="light-icon" />
              <Typography variant="h6" color="inherit" className="white-title form-text">
                Please check your Email<br /> for your temporary password.
              </Typography>
            </Toolbar>
          </AppBar>
        }
        <img src={BlueLogoImage} className="form-logo top-margin-15" style={{ float: "left" }} />
        <div style={{ clear: "both" }}></div>

        <div className="form-text">
          <Typography variant="h5" className="green-title" style={{ marginBottom: "30px" }}>Welcome to Imagus Face Recognition</Typography>
          {stage === 1 &&
            <Typography variant="subtitle1" className="green-title" style={{ marginBottom: "30px" }}>Please fill in your information and set the new password.</Typography>
          }
        </div>
        <form onSubmit={(!loading && this.submit) || undefined}>
          <Grid container spacing={8}>
            {stage === 0 &&
              <Grid item xs={12}>
                <Grid container spacing={8}>
                  <Grid item xs={1} className="icon-block">
                    <EmailIcon className="grey-icon medium-icon" />
                  </Grid>
                  <Grid item xs={11}>
                    <SingleTextField
                      isRequired={true}
                      label="Password"
                      name="tempPassword"
                      value={this.state.tempPassword}
                      onChange={this.handleChange}
                      inpuType="text"
                      isInvalid={errors.credentials !== undefined}
                    />
                  </Grid>
                </Grid>
              </Grid>
              ||
              <Grid item xs={12}>
                <Grid container spacing={8}>
                  <Grid item xs={1} className="icon-block">
                    <PersonIcon className="grey-icon medium-icon" />
                  </Grid>
                  <Grid item xs={11} md={5}>
                    <SingleTextField
                      isRequired={true}
                      label="First Name"
                      name="firstName"
                      value={this.state.firstName}
                      onChange={this.handleChange}
                      inpuType="text"
                    />
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11} md={5}>
                    <SingleTextField
                      isRequired={true}
                      label="Last Name"
                      name="lastName"
                      value={this.state.lastName}
                      onChange={this.handleChange}
                      inpuType="text"
                    />
                  </Grid>
                  <Grid item xs={1} className="icon-block">
                    <LockOutlinedIcon className="grey-icon medium-icon" />
                  </Grid>
                  <Grid item xs={11} md={5}>
                    <SingleTextField
                      isRequired={true}
                      label="New Password"
                      name="password"
                      value={this.state.password}
                      onChange={this.handleChange}
                      inpuType="password"
                    />
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11} md={5}>
                    <SingleTextField
                      isRequired={true}
                      label="Confirm Password"
                      name="password2"
                      value={this.state.password2}
                      onChange={this.handleChange}
                      inpuType="password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordStrengthBox
                      passwordstrength={this.state.passwordstrength}
                      password={this.state.password}
                    />
                  </Grid>
                </Grid>
              </Grid>
            }
            <Grid item xs={12}>
              <ErrosDisplay errors={errors} form="invite form" />
            </Grid>
            <Grid item xs={12}>
              <div className="form-right-btn-block">
                <SubmitButton
                  loading={loading}
                  title="Submit"
                  disabled={Object.keys(errors).length > 0 || (Object.values(this.state.passwordstrength).includes(false) && stage === 1)}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators({ fetchAuthentication, userLogin, userConfirm }, dispatch)
});

const InviteRedux = connect(null, mapDispatchToProps)(InviteForm);

class Invite extends Component<*> {
  render(): React$Element<*> {
    return (
      <InviteRedux match={this.props.match} />
    );
  }
}

export default Invite;
